import React, { useState, useEffect } from 'react';
import {
  Search,
  RefreshCw,
  AlertCircle,
  Building2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Plus
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import PropertyTableRow from './properties/PropertyTableRow';
import PropertyMobileCard from './properties/PropertyMobileCard';
import AddPropertyModal from './properties/AddPropertyModal';
import PropertyDetailModal from './properties/PropertyDetailModal';

const EnhancedPropertiesPage = ({ onAssignAsset, onAssetCreated }) => {
  // Auth context for role-based UI
  const { user } = useAuth();
  const isRetailManager = user?.role === 'Retail Manager';
  
  // State management
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  // Asset state management
  const [assets, setAssets] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [assetsError, setAssetsError] = useState('');
  
  // Modal states
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [showAddProperty, setShowAddProperty] = useState(false);
  const [showFloorplanViewer, setShowFloorplanViewer] = useState(false);
  const [selectedFloorplan, setSelectedFloorplan] = useState(null);
  
  // Form states
  const [propertyForm, setPropertyForm] = useState({
    building_name: '',
    sector: '',
    building_gla: '',
    status: 'Available',
    sub_category: '',
    city: '',
    province: '',
    street_address: '',
    postal_code: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    description: ''
  });

  // File upload states
  const [uploadingFloorplan, setUploadingFloorplan] = useState(false);
  const [floorplanFile, setFloorplanFile] = useState(null);
  const [floorName, setFloorName] = useState('');
  const [floorDescription, setFloorDescription] = useState('');

  const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // Validate required fields for property creation
  const validatePropertyForm = (form) => {
    const requiredFields = [
      { field: 'building_name', label: 'Building name' },
      { field: 'sector', label: 'Sector' },
      { field: 'city', label: 'City' },
      { field: 'province', label: 'Province' },
      { field: 'contact_name', label: 'Contact name' },
      { field: 'contact_email', label: 'Contact email' }
    ];

    for (const { field, label } of requiredFields) {
      if (!form[field] || !form[field].toString().trim()) {
        return { isValid: false, message: `${label} is required` };
      }
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.contact_email.trim())) {
      return { isValid: false, message: 'Please enter a valid email address' };
    }

    // Validate GLA is a positive number
    if (form.building_gla && (isNaN(form.building_gla) || parseInt(form.building_gla) <= 0)) {
      return { isValid: false, message: 'Building GLA must be a positive number' };
    }

    return { isValid: true };
  };

  // Get API configuration from localStorage
  const getApiConfig = () => {
    try {
      const token = localStorage.getItem('properties_api_token') || '';
      const endpoint = localStorage.getItem('properties_api_endpoint') || '';
      return { token, endpoint };
    } catch (error) {
      console.error('Error getting API config:', error);
      return { token: '', endpoint: '' };
    }
  };

  // Create axios instance with API token headers
  const createApiRequest = () => {
    const { token, endpoint } = getApiConfig();
    const headers = {};
    
    if (token) {
      headers['X-Properties-API-Token'] = token;
    }
    if (endpoint) {
      headers['X-Properties-API-Endpoint'] = endpoint;
    }
    
    return axios.create({
      baseURL: API,
      headers
    });
  };

  // Load properties on component mount
  useEffect(() => {
    fetchProperties();
  }, []);

  // Filter properties when search term changes
  useEffect(() => {
    filterProperties();
  }, [searchTerm, properties]);

  // Fetch properties from backend API only
  const fetchProperties = async () => {
    try {
      setLoading(true);
      setError('');
      const apiClient = createApiRequest();
      const response = await apiClient.get('/api/properties/');
      setProperties(response.data);
      setFilteredProperties(response.data);
    } catch (error) {
      setError('Failed to fetch properties from server.');
      setProperties([]);
      setFilteredProperties([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch assets for a specific property
  const fetchPropertyAssets = async (propertyId) => {
    if (!propertyId) {
      setAssets([]);
      return;
    }

    try {
      setLoadingAssets(true);
      setAssetsError('');
      const apiClient = createApiRequest();
      const response = await apiClient.get(`/api/properties/${propertyId}/assets`);
      setAssets(response.data.assets || []);
    } catch (error) {
      console.error('Failed to fetch property assets:', error);
      setAssetsError('Failed to load assets for this property.');
      setAssets([]);
    } finally {
      setLoadingAssets(false);
    }
  };

  // Filter properties based on search term
  const filterProperties = () => {
    if (!searchTerm.trim()) {
      setFilteredProperties(properties);
      return;
    }

    const filtered = properties.filter(property => {
      // Ensure property exists and is an object
      if (!property || typeof property !== 'object') {
        return false;
      }

      const searchLower = searchTerm.toLowerCase();
      
      // Helper function to safely get string value and convert to lowercase
      const getSearchableValue = (value) => {
        if (value === null || value === undefined) return '';
        return String(value).toLowerCase();
      };

      return (
        getSearchableValue(property.building_name || property.buildingName).includes(searchLower) ||
        getSearchableValue(property.sector).includes(searchLower) ||
        getSearchableValue(property.status).includes(searchLower) ||
        getSearchableValue(property.sub_category || property.subCategoryString).includes(searchLower) ||
        getSearchableValue(property.city).includes(searchLower) ||
        getSearchableValue(property.province).includes(searchLower) ||
        getSearchableValue(property.street_address).includes(searchLower) ||
        getSearchableValue(property.contact_name).includes(searchLower) ||
        getSearchableValue(property.contact_email).includes(searchLower) ||
        // Support for alternative field names from API
        getSearchableValue(property.buildingContactableList?.[0]?.name).includes(searchLower) ||
        getSearchableValue(property.buildingContactableList?.[0]?.email).includes(searchLower)
      );
    });

    setFilteredProperties(filtered);
  };

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    const sortedData = [...filteredProperties].sort((a, b) => {
      // Helper function to safely get value for sorting
      const getSortValue = (obj, key) => {
        if (!obj || typeof obj !== 'object') return '';
        
        // Handle nested properties like buildingContactableList[0].name
        if (key.includes('.')) {
          const keys = key.split('.');
          let value = obj;
          for (const k of keys) {
            value = value?.[k];
            if (value === null || value === undefined) return '';
          }
          return String(value).toLowerCase();
        }
        
        // Handle alternative field names
        let value;
        switch (key) {
          case 'building_name':
            value = obj.building_name || obj.buildingName;
            break;
          case 'sub_category':
            value = obj.sub_category || obj.subCategoryString;
            break;
          case 'building_gla':
            value = obj.building_gla || obj.buildingGla;
            break;
          default:
            value = obj[key];
        }
        
        if (value === null || value === undefined) return '';
        return String(value).toLowerCase();
      };

      const aValue = getSortValue(a, key);
      const bValue = getSortValue(b, key);

      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredProperties(sortedData);
    setSortConfig({ key, direction });
  };

  // Get sort icon
  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown size={14} className="text-gray-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp size={14} className="text-cyan-600" />
      : <ArrowDown size={14} className="text-cyan-600" />;
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'available': return 'bg-blue-100 text-blue-800';
      case 'leased': return 'bg-purple-100 text-purple-800';
      case 'under development': return 'bg-yellow-100 text-yellow-800';
      case 'sold': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Format GLA
  const formatGLA = (gla) => {
    if (!gla || gla === 0) return 'N/A';
    return new Intl.NumberFormat('en-CA').format(gla);
  };

  // Open property details modal
  const openPropertyModal = (property) => {
    setSelectedProperty(property);
    setShowPropertyModal(true);
    // Fetch assets for the selected property
    fetchPropertyAssets(property.id);
  };

  // Close property modal
  const closePropertyModal = () => {
    setSelectedProperty(null);
    setShowPropertyModal(false);
    // Clear assets when modal is closed
    setAssets([]);
    setAssetsError('');
    setLoadingAssets(false);
  };

  // Reset property form
  const resetPropertyForm = () => {
    setPropertyForm({
      building_name: '',
      sector: '',
      building_gla: '',
      status: 'Available',
      sub_category: '',
      city: '',
      province: '',
      street_address: '',
      postal_code: '',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      description: ''
    });
  };

  // Always use the fixed Azure bucket and buildingImages[0].fileName for the image URL
  const getPropertyImageUrl = (property) => {
    // 1. Prefer explicit imageBucketUrl/image_bucket_url if present
    if (property.imageBucketUrl) return property.imageBucketUrl;
    if (property.image_bucket_url) return property.image_bucket_url;
    // 2. Fallback to buildingImages[0].fileName or building_images[0].fileName
    const images = property.buildingImages || property.building_images || [];
    const bucketUrl = 'https://fortressfundeltbucket.blob.core.windows.net/public-portal-images';
    if (images.length > 0 && images[0].fileName) {
      return `${bucketUrl}/${images[0].fileName}?w=800&h=600&fit=crop`;
    }
    // 3. Fallback to a static placeholder image
    return 'https://via.placeholder.com/64x64?text=No+Image';
  };


  // Create new property (local storage only)
  const createProperty = async () => {
    console.log('Creating property locally with form data:', propertyForm);
    
    try {
      // Validate required fields
      const validation = validatePropertyForm(propertyForm);
      if (!validation.isValid) {
        toast.error(validation.message);
        console.error('Validation failed:', validation.message);
        return;
      }

      setLoading(true);
      console.log('Property validation passed, creating locally...');
      
      // Create new property with complete schema matching demo properties
      const newProperty = {
        // Use unique ID with 'user_' prefix to avoid conflicts with demo IDs
        id: `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        
        // Core property data
        building_name: propertyForm.building_name,
        buildingName: propertyForm.building_name, // Alternate field for compatibility
        sector: propertyForm.sector,
        building_gla: propertyForm.building_gla ? parseInt(propertyForm.building_gla) : 0,
        buildingGla: propertyForm.building_gla ? parseInt(propertyForm.building_gla) : 0, // Alternate field
        status: propertyForm.status || 'Available',
        sub_category: propertyForm.sub_category || '',
        subCategoryString: propertyForm.sub_category || '', // Alternate field
        city: propertyForm.city,
        province: propertyForm.province,
        street_address: propertyForm.street_address || '',
        postal_code: propertyForm.postal_code || '',
        
        // Contact information
        contact_name: propertyForm.contact_name,
        contact_email: propertyForm.contact_email,
        contact_phone: propertyForm.contact_phone || '',
        
        // Contact list format for compatibility
        buildingContactableList: [{
          name: propertyForm.contact_name,
          email: propertyForm.contact_email,
          phone: propertyForm.contact_phone || ''
        }],
        
        // Additional fields
        description: propertyForm.description || '',
        asset_count: 0,
        
        // Image data with default placeholder
        imageBucketUrl: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=600&fit=crop',
        buildingImages: [{
          fileName: 'placeholder.jpg'
        }],
        images: [],
        floorplans: [],
        
        // Timestamps
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        
        // User-added tag for identification
        user_added: true,
        source: 'User Added'
      };

      console.log('New property created:', newProperty);

      // Get current properties from state
      const currentProperties = [...properties];
      
      // Add the new property to the beginning of the list
      const updatedProperties = [newProperty, ...currentProperties];
      
      // Update state immediately
      setProperties(updatedProperties);
      setFilteredProperties(updatedProperties);
      
      // Reset form and close modal
      resetPropertyForm();
      setShowAddProperty(false);
      
      // Show success message
      toast.success('Property added successfully! Stored locally and available offline.');
      console.log(`Property added locally. Total properties: ${updatedProperties.length}`);
      
    } catch (error) {
      console.error('Error creating property:', error);
      toast.error('Failed to create property: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
      console.log('Property creation process finished');
    }
  };

  // Open asset creation form with property pre-selected
  const assignAssetToProperty = (property) => {
    if (onAssignAsset) {
      onAssignAsset(property);
      toast.success(`Asset creation form opened with ${property.building_name} pre-selected`);
    } else {
      toast.info(`Asset assignment for ${property.building_name} - Integration pending`);
    }
  };

  // Upload floorplan
  const uploadFloorplan = async () => {
    if (!floorplanFile || !selectedProperty) {
      toast.error('Please select a file to upload');
      return;
    }

    try {
      setUploadingFloorplan(true);
      
      const formData = new FormData();
      formData.append('file', floorplanFile);
      if (floorName) formData.append('floor_name', floorName);
      if (floorDescription) formData.append('description', floorDescription);

      const response = await axios.post(
        `${API}/properties/${selectedProperty.id}/floorplans`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      // Refresh property data to show new floorplan
      const updatedProperty = await axios.get(`${API}/properties/${selectedProperty.id}`);
      setSelectedProperty(updatedProperty.data);
      
      // Reset form
      setFloorplanFile(null);
      setFloorName('');
      setFloorDescription('');
      
      toast.success('Floorplan uploaded successfully!');
      
    } catch (error) {
      toast.error('Failed to upload floorplan');
      console.error('Error uploading floorplan:', error);
    } finally {
      setUploadingFloorplan(false);
    }
  };

  // Open floorplan viewer
  const viewFloorplan = (floorplan) => {
    setSelectedFloorplan(floorplan);
    setShowFloorplanViewer(true);
  };

  if (loading && properties.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="flex items-center justify-center">
          <RefreshCw className="animate-spin h-8 w-8 text-cyan-600 mr-3" />
          <span className="text-lg text-gray-600">Loading properties...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <Building2 className="mr-3 h-6 w-6 text-cyan-600" />
            {isRetailManager ? 'Properties Directory' : 'Properties Management'}
          </h2>
          <p className="text-gray-600 mt-1">
            {isRetailManager 
              ? `Viewing ${filteredProperties.length} of ${properties.length} available properties`
              : `${filteredProperties.length} of ${properties.length} properties`
            }
          </p>
          {isRetailManager && (
            <p className="text-sm text-blue-600 mt-1">
              📋 View-only access - Contact administrator for property changes
            </p>
          )}
        </div>
        <div className="flex gap-3">
          <button
            onClick={fetchProperties}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50"
          >
            <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          {!isRetailManager && (
            <button
              onClick={() => setShowAddProperty(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
            >
              <Plus size={16} className="mr-2" />
              Add Property
            </button>
          )}
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Error Loading Data</h3>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search by building name, sector, status, city, province, or sub category..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Properties Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Image
                </th>
                <th 
                  onClick={() => handleSort('building_name')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center space-x-1">
                    <span>Building Name</span>
                    {getSortIcon('building_name')}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('sector')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center space-x-1">
                    <span>Sector</span>
                    {getSortIcon('sector')}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('building_gla')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center space-x-1">
                    <span>GLA (m²)</span>
                    {getSortIcon('building_gla')}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('status')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center space-x-1">
                    <span>Status</span>
                    {getSortIcon('status')}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('city')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center space-x-1">
                    <span>Location</span>
                    {getSortIcon('city')}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Assets
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredProperties.map((property, index) => (
                <PropertyTableRow
                  key={property.id || index}
                  property={property}
                  index={index}
                  isRetailManager={isRetailManager}
                  getPropertyImageUrl={getPropertyImageUrl}
                  getStatusColor={getStatusColor}
                  formatGLA={formatGLA}
                  onOpenModal={openPropertyModal}
                  onAssignAsset={assignAssetToProperty}
                />
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="lg:hidden space-y-4 p-4">
          {filteredProperties.map((property, index) => (
            <PropertyMobileCard
              key={property.id || index}
              property={property}
              index={index}
              isRetailManager={isRetailManager}
              getPropertyImageUrl={getPropertyImageUrl}
              getStatusColor={getStatusColor}
              formatGLA={formatGLA}
              onOpenModal={openPropertyModal}
              onAssignAsset={assignAssetToProperty}
            />
          ))}
        </div>

        {/* No Results Message */}
        {filteredProperties.length === 0 && searchTerm && (
          <div className="text-center py-8">
            <Search className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No matching results</h3>
            <p className="text-gray-600">
              No properties match your search criteria. Try adjusting your search term.
            </p>
          </div>
        )}
      </div>

      {/* Add Property Modal */}
      {showAddProperty && (
        <AddPropertyModal
          propertyForm={propertyForm}
          setPropertyForm={setPropertyForm}
          loading={loading}
          onClose={() => {
            setShowAddProperty(false);
            resetPropertyForm();
          }}
          onCreateProperty={createProperty}
        />
      )}

      {/* Property Details Modal */}
      {showPropertyModal && selectedProperty && (
        <PropertyDetailModal
          selectedProperty={selectedProperty}
          assets={assets}
          loadingAssets={loadingAssets}
          assetsError={assetsError}
          getPropertyImageUrl={getPropertyImageUrl}
          getStatusColor={getStatusColor}
          formatGLA={formatGLA}
          API={API}
          onClose={closePropertyModal}
        />
      )}

    </div>
  );
};

export default EnhancedPropertiesPage;